import { CanAccess } from '@modules/common/components/CanAccess';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { EditIntegrationDrawer } from '@modules/integrationsV2/components/EditIntegrationDrawer';
import { IntegrationsActionType } from '@modules/integrationsV2/store/integrationsActionType';
import { useIntegrationsStore } from '@modules/integrationsV2/store/integrationsStore';
import {
  getIsEditableIntegration,
  getIsIntegrationEditOptional,
  trackIntegrationEvent,
  trackIntegrationPageEvent,
} from '@modules/integrationsV2/utils';
import { EditRounded } from '@mui/icons-material';
import { Button, ButtonProps } from '@mui/material';
import {
  IntegrationType,
  IntegrationViewFragment,
  PermissionOperation,
  PermissionResource,
} from 'generated/graphql';
import { CohortIntegrationUserIdentifiersForm } from 'modules/ui/Settings/Integrations/CohortIntegrationUserIdentifiersForm';
import { useEffect, useState } from 'react';

interface EditIntegrationCTAProps {
  integration: IntegrationViewFragment;
  buttonVariant?: ButtonProps['variant'];
}

export const EditIntegrationCTA = ({
  integration,
  buttonVariant = 'text',
}: EditIntegrationCTAProps) => {
  const { openModal } = useMuiModalContext();

  const selectedIntegrationIDToUpdate = useIntegrationsStore(
    ({ view }) => view.selectedIntegrationIDToUpdate,
  );
  const dispatch = useIntegrationsStore(({ dispatch }) => dispatch);

  const [isEditIntegrationDrawerOpen, setIsEditIntegrationDrawerOpen] =
    useState(false);

  const {
    channel: { type: channelType },
    id: integrationID,
    isActive,
    data: integrationData,
    integrationType,
    description,
  } = integration;

  const isEditableIntegration = getIsEditableIntegration({
    channelType,
    integrationData: integrationData,
    integrationType,
  });

  // TODO: Remove after integration context feature flag is removed
  const isIntegrationEditOptional = getIsIntegrationEditOptional({
    channelType,
  });

  const shouldOpenEditIntegrationDrawer =
    selectedIntegrationIDToUpdate === integrationID &&
    isEditableIntegration &&
    !isIntegrationEditOptional;

  // Open edit integration drawer after an integration is added
  useEffect(() => {
    if (shouldOpenEditIntegrationDrawer) {
      setIsEditIntegrationDrawerOpen(true);
      dispatch({
        type: IntegrationsActionType.SET_VIEW,
        payload: {
          selectedIntegrationIDToUpdate: null,
        },
      });
    }
  }, [dispatch, shouldOpenEditIntegrationDrawer]);

  if (
    (integration.integrationType !== IntegrationType.Cohorts &&
      !isEditableIntegration) ||
    !isActive
  ) {
    return null;
  }

  const closeEditIntegrationDrawer = () => {
    setIsEditIntegrationDrawerOpen(false);
    trackIntegrationEvent({
      eventName: 'Edit Integration Sidebar Closed',
      integrationChannelType: channelType,
    });
  };

  const openEditIntegrationDrawer = () => {
    if (integration.integrationType === IntegrationType.Cohorts) {
      openModal({
        contentComponent: () => (
          <CohortIntegrationUserIdentifiersForm channelType={channelType} />
        ),
        modalProps: {
          fullWidth: true,
          maxWidth: 'lg',
          PaperProps: {
            elevation: 3,
          },
        },
      });
      trackIntegrationPageEvent({
        eventName: 'Integrations - Edit Button Clicked',
      });
      return;
    }

    setIsEditIntegrationDrawerOpen(true);
    trackIntegrationPageEvent({
      eventName: 'Integrations - Edit Button Clicked',
    });
    trackIntegrationEvent({
      eventName: 'Edit Integration Sidebar Opened',
      integrationChannelType: channelType,
    });
  };

  return (
    <>
      <CanAccess
        resource={PermissionResource.Integration}
        operation={PermissionOperation.Write}
      >
        <Button
          size="small"
          variant={buttonVariant}
          startIcon={<EditRounded />}
          onClick={openEditIntegrationDrawer}
        >
          Edit
        </Button>
      </CanAccess>
      <EditIntegrationDrawer
        integrationChannel={integration.channel}
        isOpen={isEditIntegrationDrawerOpen}
        onDrawerClose={closeEditIntegrationDrawer}
        integrationID={integrationID}
        integrationData={integrationData}
        integrationType={integrationType}
        namespace={integration.namespace}
        description={description?.description ?? ''}
      />
    </>
  );
};
